import { useEffect, useState } from "react";
import {
  Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Line, ComposedChart, CartesianGrid, Legend,
} from "recharts";
import { Layers, TrendingUp, Package, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo } from "@/lib/navigation";

/* ---------- Fictional demo data (illustrative only, not client data) ---------- */
const REVENUE = {
  months: { en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"], zh: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月"] },
  branches: { en: ["Melbourne", "Sydney", "Brisbane"], zh: ["墨尔本", "悉尼", "布里斯班"] },
  a: [0.7, 1.4, 1.7, 1.5, 1.5, 1.6, 1.6, 1.4],
  b: [1.0, 1.5, 1.9, 1.7, 1.7, 1.8, 1.6, 1.7],
  c: [0.6, 0.9, 1.2, 1.3, 1.1, 1.1, 1.3, 1.3],
  margin: [34.9, 34.3, 36.3, 39.3, 38.8, 37.9, 35.5, 32.7],
};

const INVENTORY = {
  en: [
    [
      { sku: "DEMO-001", name: "Oakridge Dining Table", cls: "Furniture", s30: 46, s90: 132, rr: 44.6, status: "healthy" },
      { sku: "DEMO-002", name: "Nordic Lounge Chair", cls: "Furniture", s30: 212, s90: 580, rr: 196.1, status: "excess" },
      { sku: "DEMO-003", name: "Aurora Floor Lamp", cls: "Lighting", s30: 88, s90: 249, rr: 84.2, status: "healthy" },
      { sku: "DEMO-004", name: "Linen Queen Bedframe", cls: "Bedroom", s30: 31, s90: 97, rr: 32.8, status: "healthy" },
      { sku: "DEMO-005", name: "Studio Desk 1400", cls: "Office", s30: 9, s90: 74, rr: 25.0, status: "short" },
    ],
    [
      { sku: "DEMO-006", name: "Harbour Outdoor Sofa", cls: "Outdoor", s30: 27, s90: 81, rr: 27.4, status: "healthy" },
      { sku: "DEMO-007", name: "Pendant Light Trio", cls: "Lighting", s30: 154, s90: 402, rr: 135.9, status: "excess" },
      { sku: "DEMO-008", name: "Ergo Task Chair", cls: "Office", s30: 6, s90: 58, rr: 19.6, status: "short" },
      { sku: "DEMO-009", name: "Walnut Bookshelf", cls: "Furniture", s30: 63, s90: 178, rr: 60.2, status: "healthy" },
      { sku: "DEMO-010", name: "Cotton Bedside Table", cls: "Bedroom", s30: 41, s90: 119, rr: 40.2, status: "healthy" },
    ],
  ],
  zh: [
    [
      { sku: "DEMO-001", name: "橡木餐桌", cls: "家具", s30: 46, s90: 132, rr: 44.6, status: "healthy" },
      { sku: "DEMO-002", name: "北欧休闲椅", cls: "家具", s30: 212, s90: 580, rr: 196.1, status: "excess" },
      { sku: "DEMO-003", name: "极光落地灯", cls: "灯具", s30: 88, s90: 249, rr: 84.2, status: "healthy" },
      { sku: "DEMO-004", name: "亚麻双人床架", cls: "卧室", s30: 31, s90: 97, rr: 32.8, status: "healthy" },
      { sku: "DEMO-005", name: "工作室书桌 1400", cls: "办公", s30: 9, s90: 74, rr: 25.0, status: "short" },
    ],
    [
      { sku: "DEMO-006", name: "海港户外沙发", cls: "户外", s30: 27, s90: 81, rr: 27.4, status: "healthy" },
      { sku: "DEMO-007", name: "三头吊灯", cls: "灯具", s30: 154, s90: 402, rr: 135.9, status: "excess" },
      { sku: "DEMO-008", name: "人体工学办公椅", cls: "办公", s30: 6, s90: 58, rr: 19.6, status: "short" },
      { sku: "DEMO-009", name: "胡桃木书架", cls: "家具", s30: 63, s90: 178, rr: 60.2, status: "healthy" },
      { sku: "DEMO-010", name: "棉麻床头柜", cls: "卧室", s30: 41, s90: 119, rr: 40.2, status: "healthy" },
    ],
  ],
};

const VIEWS = ["consolidated", "inventory", "kpi"];
const BULLET_ICONS = [Layers, TrendingUp, Package, DollarSign];

export default function AnalyticsShowcase() {
  const { t, lang } = useLang();
  const a = t.analytics;
  const [view, setView] = useState(0);
  const [invSet, setInvSet] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setView((v) => (v + 1) % VIEWS.length), 4500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (VIEWS[view] !== "inventory") return;
    const id = setInterval(() => setInvSet((s) => (s + 1) % 2), 1800);
    return () => clearInterval(id);
  }, [view]);

  const scrollTo = useGoTo();

  const [b1, b2, b3] = REVENUE.branches[lang];
  const marginKey = a.chart.margin;
  const chartData = REVENUE.months[lang].map((m, i) => ({
    month: m, [b1]: REVENUE.a[i], [b2]: REVENUE.b[i], [b3]: REVENUE.c[i], [marginKey]: REVENUE.margin[i],
  }));
  const rows = INVENTORY[lang][invSet];

  return (
    <section id="analytics" data-testid="analytics-showcase" className="section-y bg-ink-900 text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand-500/20 blur-3xl -z-0" />
      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-14 items-center">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="hairline" />
            <span className="text-overline" style={{ color: "#F8AE3E" }}>{a.overline}</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white">
            {a.h2a}{" "}
            <span className="text-brand-500">{a.h2accent}</span> {a.h2b}
          </h2>
          <p className="mt-6 text-base text-white/70 leading-relaxed">{a.sub}</p>

          <ul className="mt-8 space-y-4">
            {a.bullets.map((txt, i) => {
              const Icon = BULLET_ICONS[i];
              return (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-8 h-8 grid place-items-center bg-brand-500/15 text-brand-400 shrink-0 mt-0.5">
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <span className="text-sm text-white/80">{txt}</span>
                </li>
              );
            })}
          </ul>

          <Button
            data-testid="analytics-cta"
            onClick={() => scrollTo("#book")}
            className="mt-10 group bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-0.5 transition-all duration-300 rounded-sm h-12 px-7 text-sm tracking-wide font-semibold shadow-sm"
          >
            {a.cta}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>

        {/* Right animated dashboard */}
        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-3 border-2 border-brand-500/40 hidden sm:block" style={{ transform: "translate(12px,12px)" }} />
            <div className="relative bg-[#0F1626] border border-white/10 shadow-2xl">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 h-9 border-b border-white/10 bg-black/30">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                <div className="ml-4 text-[11px] uppercase tracking-widest text-white/40">{a.win[VIEWS[view]]}</div>
                <div className="ml-auto flex gap-1">
                  {VIEWS.map((_, i) => (
                    <span key={i} data-testid={`analytics-tab-${i}`} className={`h-1 w-6 transition-all ${i === view ? "bg-brand-500" : "bg-white/15"}`} />
                  ))}
                </div>
              </div>

              <div className="p-4 sm:p-6 min-h-[420px]" key={view}>
                {VIEWS[view] === "consolidated" && (
                  <div className="animate-fade-up">
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-white/50">{a.chart.title}</div>
                        <div className="text-xs text-white/40">{a.chart.sub}</div>
                      </div>
                      <div className="text-xs text-white/60">{a.chart.range}</div>
                    </div>
                    <div style={{ width: "100%", height: 340 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={chartData} margin={{ top: 12, right: 20, left: -10, bottom: 0 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                          <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}M`} width={52} />
                          <YAxis yAxisId="right" orientation="right" stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 11 }} unit="%" domain={[20, 50]} />
                          <Tooltip
                            contentStyle={{ background: "#0F1626", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                            labelStyle={{ color: "#fff" }}
                            cursor={{ fill: "rgba(244,155,31,0.08)" }}
                            formatter={(value, name) => (name === marginKey ? [`${value}%`, name] : [`$${value}M`, `${name} ${a.chart.revenue}`])}
                          />
                          <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }} />
                          <Bar yAxisId="left" dataKey={b1} stackId="a" fill="#7FB7FF" isAnimationActive animationDuration={900} />
                          <Bar yAxisId="left" dataKey={b2} stackId="a" fill="#F8AE3E" isAnimationActive animationDuration={900} />
                          <Bar yAxisId="left" dataKey={b3} stackId="a" fill="#2E6CD9" isAnimationActive animationDuration={900} />
                          <Line yAxisId="right" type="monotone" dataKey={marginKey} stroke="#22C55E" strokeWidth={2.4} dot={{ r: 3 }} isAnimationActive animationDuration={1200} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {VIEWS[view] === "inventory" && (
                  <div className="animate-fade-up">
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-white/50">{a.inv.title}</div>
                        <div className="text-xs text-white/40">{a.inv.sub}</div>
                      </div>
                      <div className="text-xs text-white/60">{a.inv.tag}</div>
                    </div>
                    <div className="overflow-hidden border border-white/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-white/5 text-white/60 uppercase tracking-widest text-[10px]">
                            {a.inv.cols.map((c, i) => (
                              <th key={i} className={`px-3 py-2 font-semibold ${i < 3 ? "text-left" : "text-right"}`}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody key={invSet}>
                          {rows.map((r, i) => (
                            <tr key={r.sku} className="border-t border-white/5 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                              <td className="px-3 py-2 font-mono text-white/70 whitespace-nowrap">{r.sku}</td>
                              <td className="px-3 py-2 text-white/90">{r.name}</td>
                              <td className="px-3 py-2 text-white/60">{r.cls}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-white/80">{r.s30}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-white/80">{r.s90.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right tabular-nums font-semibold text-white">{r.rr.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right">
                                <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap ${
                                  r.status === "excess" ? "bg-yellow-400/15 text-yellow-300" :
                                  r.status === "short" ? "bg-red-400/15 text-red-300" : "bg-green-400/15 text-green-300"
                                }`}>
                                  {a.inv.status[r.status]}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[11px] text-white/50">
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-green-400" />{a.inv.legend[0]}</span>
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-yellow-400" />{a.inv.legend[1]}</span>
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-red-400" />{a.inv.legend[2]}</span>
                    </div>
                  </div>
                )}

                {VIEWS[view] === "kpi" && (
                  <div className="animate-fade-up">
                    <div className="flex items-baseline justify-between mb-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-white/50">{a.kpi.title}</div>
                        <div className="text-xs text-white/40">{a.kpi.sub}</div>
                      </div>
                      <div className="text-xs text-white/60">{a.kpi.tag}</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {a.kpi.items.map((k, i) => (
                        <div key={i} className="bg-black/40 border border-white/10 p-4 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                          <div className="text-[10px] uppercase tracking-widest text-white/50">{k.label}</div>
                          <div className="mt-2 font-display font-extrabold text-2xl text-white tabular-nums">{k.v}</div>
                          <div className={`mt-1 text-[11px] ${i === 4 ? "text-yellow-300" : "text-green-400"}`}>{k.delta}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 p-4 border border-brand-500/30 bg-brand-500/5 text-sm text-white/80">
                      <span className="text-brand-400 font-semibold">{a.kpi.insightLabel}</span> {a.kpi.insight}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
